import React, { useContext } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { Context as AuthContext } from '../../contexts/AuthContext'
import { useAccountingBook, useAccountingBookSummary, usePayments, useHistory, useIsDesktop } from '../../hooks'
import { Loading, PaymentsHeader, SettleGroups, DisclaimerBox } from '../../components'
import formatAmount from '../../utilities/formatAmount'
import { themeColors, userImageUrls, gradients } from '../../constants'
import styles from './OverviewPage.module.scss'

const pageStyles = {
  bg: {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
    height: 'calc(100vh)',
    maxHeight: '-webkit-fill-available',
    overflow: 'hidden',
    display: 'flex',
    flexFlow: 'column',
    position: 'relative',
  },
}

const Avatar = ({ url }) => (
  <img className={styles.avatar} src={url || userImageUrls[0]} alt="" />
)

const tintClass = (d) => (d > 0 ? styles.pos : d < 0 ? styles.neg : styles.zero)

const EmptyState = ({ icon, title, desc }) => (
  <div className={styles.emptyCard}>
    <div className={styles.emptyIcon}>{icon}</div>
    <div className={styles.emptyTitle}>{title}</div>
    <div className={styles.emptyDesc}>{desc}</div>
  </div>
)

const DoubleCheckIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M1.5 13l4 4 8-9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 13l4 4 9.5-11" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const MembersIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="2" />
    <path d="M3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 5.5a3 3 0 010 5.6M17.5 19.5c0-2.4-1.2-4.2-3-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

// Book home (hub): group total → 明細, 分帳建議 (grouped) → settle,
// 成員收支 (everyone) → member detail, ＋新增帳款.
const OverviewPage = () => {
  const { state: authState } = useContext(AuthContext)
  const { group_id, accounting_book_id } = useParams()
  const location = useLocation()
  const backLink = location.state && location.state.backLink
  const history = useHistory()
  const isDesktop = useIsDesktop()
  // eslint-disable-next-line
  const [_cookies, setCookie] = useCookies(['payment'])

  /* eslint-disable no-unused-vars */
  const [_users, accountingBookDetails, _abLoading, _abErr, _setUsers, loadAccountingBook] = useAccountingBook(authState)
  const [summary, loading, _err, _get, _debt, settlements] = useAccountingBookSummary(authState)
  const [payments] = usePayments(authState, { touched: false, owers: { value: [] }, payers: { value: [] } }, "")

  const symbol = accountingBookDetails.currency_symbol || '$'
  const exponent = accountingBookDetails.exponent || 0
  const paymentsSize = payments.length
  const peopleSize = accountingBookDetails.cover_cost_users_size || 0
  const myId = authState.userLineIdToken
  const totalSpent = (settlements || []).reduce((s, x) => s + (Number(x.acc) || 0), 0)

  // Prompt to set this book as the group default so chat commands target it.
  // Only shown once details have loaded and the book isn't already the default.
  const handleSetAsDefault = () => {
    authState.api.updateCurrentAccountingBook(group_id, accounting_book_id)
      .then(() => loadAccountingBook())
      .catch((err) => console.log(err))
  }
  const disclaimerBanner = accountingBookDetails.current === false && (
    <div className={styles.disclaimer}>
      <DisclaimerBox closed={handleSetAsDefault}>
        如需於聊天室中使用帳款指令請將此帳本設為預設
      </DisclaimerBox>
    </div>
  )

  const base = `/liff_entry/groups/${group_id}/accounting_books/${accounting_book_id}`
  const goList = () => history.navigate(`${base}/payments/index`)
  const goSummary = () => history.navigate(`${base}/payments/summary`)
  const goMember = (uid) => history.navigate(`${base}/members/${uid}/payments`)
  const goNew = () => history.navigate(`${base}/payments/new`)

  // settle plan grouped by 收款者 (creditor)
  const groups = (summary || []).map((t) => ({
    payer: { id: t.payer_id, name: t.payer_display_name, img: t.payer_image_url },
    total: t.total,
    debts: (t.debts || []).map((d) => ({
      ower: { id: d.ower_id, name: d.ower_display_name, img: d.ower_image_url },
      amount: d.amount,
    })),
  }))
  const topGroups = groups.slice(0, 2)

  const markPaid = (tr) => {
    setCookie('payment', {
      description: '還款',
      amount: tr.amount,
      paid_back: true,
      allocation_type: 'amount',
      payer_id: tr.from.id,
      ower_id: tr.to.id,
    }, { path: '/' })
    history.navigate(`${base}/payments/paid_back`)
  }

  // 成員收支 — you first, then others by balance
  const meSettlement = (settlements || []).find((s) => String(s.user_id) === String(myId))
  const others = (settlements || [])
    .filter((s) => String(s.user_id) !== String(myId))
    .sort((a, b) => (Number(b.debt) || 0) - (Number(a.debt) || 0))
  const members = meSettlement ? [meSettlement, ...others] : others

  // ---- desktop analytics (computed from the same payments we already load) ----
  const spendable = (payments || []).filter((p) => !p.paid_back)

  // 這趟你花了 — the current user's share of all spending: sum of allocations
  // owed by them (their portion of each bill), regardless of who fronted it (代墊).
  // Excludes paybacks since those aren't in `spendable`.
  const mySpent = spendable.reduce((sum, p) => {
    const mine = (p.allocations || []).filter(
      (a) => a.owned_by_current || String(a.ower_id) === String(myId)
    )
    return sum + mine.reduce((s, a) => s + (Number(a.amount) || 0), 0)
  }, 0)

  // 每日花費 — sum by day, keep the most recent 14 days present in the data
  const dailyMap = new Map()
  spendable.forEach((p) => {
    const k = p.created_at_group
    dailyMap.set(k, (dailyMap.get(k) || 0) + (Number(p.amount) || 0))
  })
  // Show a continuous one-week window ending on the most recent spending day,
  // filling days with no spending as 0 so the chart reads as a real timeline.
  const DAILY_WINDOW = 7
  const dayKeys = Array.from(dailyMap.keys()).sort()
  const dailyEnd = dayKeys[dayKeys.length - 1]
  const daily = []
  if (dailyEnd) {
    const anchor = new Date(`${dailyEnd}T00:00:00`)
    for (let i = DAILY_WINDOW - 1; i >= 0; i--) {
      const dt = new Date(anchor)
      dt.setDate(anchor.getDate() - i)
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
      daily.push({ date: key, amt: dailyMap.get(key) || 0 })
    }
  }
  const dailyMax = Math.max(1, ...daily.map((d) => d.amt))

  // 成員貢獻 — who actually paid (handles multi-payer rows)
  const contribMap = new Map()
  spendable.forEach((p) => {
    if (p.multi_payer && p.payment_payers) {
      p.payment_payers.forEach((pp) => {
        contribMap.set(pp.user_display_name, (contribMap.get(pp.user_display_name) || 0) + (Number(pp.amount) || 0))
      })
    } else {
      const name = p.payer_display_name || '—'
      contribMap.set(name, (contribMap.get(name) || 0) + (Number(p.amount) || 0))
    }
  })
  const contrib = Array.from(contribMap.entries())
    .map(([name, amt]) => ({ name, amt }))
    .sort((a, b) => b.amt - a.amt)
    .slice(0, 6)
  const contribMax = Math.max(1, ...contrib.map((c) => c.amt))

  const statStrip = (
    <div className={styles.statStrip}>
      <div className={styles.statBox}>
        <div className={styles.statLabel}>總支出</div>
        <div className={styles.statValue}>{formatAmount(totalSpent, exponent, symbol)}</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statLabel}>帳款數量</div>
        <div className={styles.statValue}>{paymentsSize}</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statLabel}>待結清</div>
        <div className={styles.statValue}>{groups.length} <span className={styles.statUnit}>筆</span></div>
      </div>
    </div>
  )

  const dailyChart = daily.length > 0 && (
    <div className={styles.section}>
      <div className={styles.cardHead}><span className={styles.cardTitle}>每日花費</span></div>
      <div className={styles.chartCard}>
        <div className={styles.barChart}>
          {daily.map((d) => (
            <div className={styles.barCol} key={d.date} title={`${d.date}・${formatAmount(d.amt, exponent, symbol)}`}>
              <div className={styles.barTrack}>
                <div className={styles.barFill} style={{ height: d.amt > 0 ? `${Math.max(6, (d.amt / dailyMax) * 100)}%` : '0%' }} />
              </div>
              <div className={styles.barTick}>{(d.date || '').slice(5)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const contribChart = contrib.length > 0 && (
    <div className={styles.section}>
      <div className={styles.cardHead}><span className={styles.cardTitle}>成員貢獻</span></div>
      <div className={styles.chartCard}>
        {contrib.map((c) => (
          <div className={styles.contribRow} key={c.name}>
            <span className={styles.contribName}>{c.name}</span>
            <div className={styles.contribBarTrack}>
              <div className={styles.contribBarFill} style={{ width: `${(c.amt / contribMax) * 100}%` }} />
            </div>
            <span className={styles.contribAmt}>{formatAmount(c.amt, exponent, symbol)}</span>
          </div>
        ))}
      </div>
    </div>
  )

  // On desktop there is room to show every settle suggestion; on mobile we cap
  // to the top 2 and link out to the full settle page.
  const settleData = isDesktop ? groups : topGroups

  const heroCard = (
    <button
      type="button"
      className={styles.balanceCard}
      style={{ background: gradients.brandGreen }}
      onClick={goList}
    >
      <div className={styles.balanceLabel}>這趟你花了這些</div>
      <div className={styles.balanceRow}>
        <span className={styles.balanceAmount}>{formatAmount(mySpent, exponent, symbol)}</span>
      </div>
      <div className={styles.balanceMeta}>
        <span>{peopleSize} 人分帳・{paymentsSize} 筆帳款</span>
        <span className={styles.linkText}>查看明細 ›</span>
      </div>
    </button>
  )

  const settleSection = (
    <div className={styles.section}>
      <div className={styles.cardHead}>
        <span className={styles.cardTitle}>分帳建議</span>
      </div>
      <div className={styles.cardSub}>以下是最簡單的結算方式，幫助大家快速還清款項</div>
      {groups.length === 0 ? (
        <EmptyState
          icon={<DoubleCheckIcon />}
          title="目前沒有要還的款"
          desc="龍寶寶會用最簡單的方式幫大家結算"
        />
      ) : (
        <>
          <div className={styles.settleWrap}>
            <SettleGroups groups={settleData} symbol={symbol} exponent={exponent} onMarkPaid={markPaid} />
          </div>
          {!isDesktop && groups.length > topGroups.length && (
            <div className={styles.moreHint} onClick={goSummary}>還有 {groups.length - topGroups.length} 筆 ›</div>
          )}
        </>
      )}
    </div>
  )

  const memberSection = (
    <div className={`${styles.section} ${styles.grow}`}>
      <div className={styles.cardHead}>
        <span className={styles.cardTitle}>每個人的花費</span>
      </div>
      <div className={styles.cardSub}>成員在此帳本的總支出</div>
      {members.length === 0 ? (
        <EmptyState
          icon={<MembersIcon />}
          title="還沒有任何分帳"
          desc="新增帳款後，每個人的總支出都會列在這裡"
        />
      ) : (
      <div className={styles.memberList}>
        {[...members].sort((a, b) => (String(b.user_id) === String(myId)) - (String(a.user_id) === String(myId))).map((m) => {
          const spent = Number(m.acc) || 0
          return (
            <div
              className={styles.memberRow}
              key={m.user_id}
              onClick={() => goMember(m.user_id)}
            >
              <Avatar url={m.image_url} />
              <span className={styles.memberName}>{m.user_display_name}</span>
              <span className={styles.memberAmt}>
                {formatAmount(spent, exponent, symbol)}
              </span>
              <span className={styles.chev}>›</span>
            </div>
          )
        })}
      </div>
      )}
    </div>
  )

  return (
    <div style={isDesktop ? { ...pageStyles.bg, maxWidth: 'none', height: '100%' } : pageStyles.bg}>
      { !isDesktop && <PaymentsHeader accountingBookDetails={accountingBookDetails} backLink={backLink} /> }

      {loading ? <Loading /> : isDesktop ? (
        <div className={styles.desktopScroll}>
          {disclaimerBanner}
          {statStrip}
          <div className={styles.desktopGrid}>
            <div className={styles.colMain}>
              {heroCard}
              {dailyChart}
              {settleSection}
            </div>
            <div className={styles.colSide}>
              {memberSection}
              {contribChart}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.scroll}>
            {disclaimerBanner}
            {heroCard}
            {settleSection}
            {memberSection}
          </div>

          {/* primary action pinned to the bottom */}
          <div className={styles.footer}>
            <button type="button" className={styles.addCard} onClick={goNew}>＋ 新增帳款</button>
          </div>
        </>
      )}
    </div>
  )
}

export default OverviewPage
